/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, Typography, Box, MenuItem, Select, SelectChangeEvent, InputLabel, FormControl, TextField, useTheme, Snackbar, Alert, alpha, CircularProgress, Backdrop, IconButton, LinearProgress } from '@mui/material';
import { AutoCompleteContaBancariaSimple } from '../../../shared/forms';
import { FaturamentoService } from '../../../shared/services/api/Financeiro/Faturamento';
import * as yup from 'yup';
import { Environment } from '../../../shared/environment';
import { ConfiguracaoPdvService } from '../../../shared/services/api/cadastros/ConfiguracaoPdv';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/ContaBancaria';
import { PaymentMethodSelector, IFormaPagamentoOpcao } from './PaymentMethodSelector';
import { DocumentoResumoBar } from './DocumentoResumoBar';
import { FormaProntaPanel } from './FormaProntaPanel';
import { KeyHintRow } from './KeyHint';
import { isEventoDeDigitacao } from './keyboardUtils';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloseIcon from '@mui/icons-material/Close';
import MoneyIcon from '@mui/icons-material/Money';
import PixIcon from '@mui/icons-material/Pix';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BlockIcon from '@mui/icons-material/Block';
import FactCheckIcon from '@mui/icons-material/FactCheck';

export interface IPagamentoData {
  id: string;
  idconta: string;
  idparceiro: string;
  indpag: number;
  tpag: string;
  tcnpj?: string;
  tband?: string;
  caut?: string;
  promocao: boolean;
}

export interface PaymentDialogProps {
  open: boolean;
  selectedtpag?: number;
  idpar: string | null;
  selectedId: string | null;
  numerodocumento?: number | string;
  valortotal?: number;
  parceironome?: string;
  emissaodata?: string;
  onClose: (value?: string) => void;
}

// Schema preservado exatamente como estava — NFe é mais permissiva que
// NFC-e de propósito (tband/caut sempre opcionais, mesmo em cartão; ver
// comentário na versão antiga deste arquivo antes da refatoração de UI).
const pagamentoSchema = yup.object().shape({
  id: yup.string().required('O ID é obrigatório'),
  idparceiro: yup.string().required('O ID é obrigatório'),
  idconta: yup.string().required('Cadastre uma conta Padrão bancária'),
  indpag: yup.number().required('Informe o tipo de pagamento'),
  tpag: yup.string().required('Selecione a forma de pagamento'),
  tcnpj: yup.string().optional().default(''),
  tband: yup.string().optional().default('00'),
  caut: yup.string().optional().default('00'),
  promocao: yup.boolean().required(),
});

const FullScreenLoading: React.FC<{ message?: string; progress?: number }> = ({
  message = "Emitindo nota fiscal...",
  progress
}) => {
  return (
    <Backdrop
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3
      }}
      open={true}
    >
      <CircularProgress
        size={60}
        sx={{
          color: 'white',
          '& .MuiCircularProgress-circle': {
            strokeLinecap: 'round',
          }
        }}
      />
      <Typography
        variant="h6"
        sx={{
          color: 'white',
          fontWeight: 500,
          textAlign: 'center'
        }}
      >
        {message}
      </Typography>
      {progress !== undefined && progress > 0 && (
        <Box sx={{ width: '300px', mt: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white'
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5, display: 'block', textAlign: 'center' }}>
            {Math.round(progress)}%
          </Typography>
        </Box>
      )}
      <Typography
        variant="body2"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          textAlign: 'center'
        }}
      >
        Aguarde enquanto processamos sua solicitação...
      </Typography>
    </Backdrop>
  );
};

const TODAS_FORMAS_PAGAMENTO: IFormaPagamentoOpcao[] = [
  { id: '01', nome: 'Dinheiro', icon: <MoneyIcon fontSize="small" /> },
  { id: '17', nome: 'PIX Dinâmico', icon: <PixIcon fontSize="small" /> },
  { id: '20', nome: 'PIX Estático', icon: <PixIcon fontSize="small" /> },
  { id: '04', nome: 'Cartão de Débito', icon: <CreditCardIcon fontSize="small" /> },
  { id: '03', nome: 'Cartão de Crédito', icon: <CreditCardIcon fontSize="small" /> },
  { id: '18', nome: 'Transferência', icon: <SwapHorizIcon fontSize="small" /> },
  { id: '90', nome: 'Sem Pagamento', icon: <BlockIcon fontSize="small" /> },
];

const BANDEIRAS_CARTAO = [
  { id: '01', nome: 'Visa', cor: '#1a1f71', corLight: '#2a35a1' },
  { id: '02', nome: 'Mastercard', cor: '#eb001b', corLight: '#ff1a2e' },
  { id: '06', nome: 'Elo', cor: '#00a4e0', corLight: '#1ac4ff' },
  { id: '07', nome: 'Hipercard', cor: '#f47b00', corLight: '#ff9b2e' },
  { id: '03', nome: 'American Express', cor: '#2e77bc', corLight: '#4e97dc' },
  { id: '04', nome: 'Sorocred', cor: '#2e77bc', corLight: '#4e97dc' },
  { id: '05', nome: 'Diners Club', cor: '#2e77bc', corLight: '#4e97dc' },
  { id: '08', nome: 'Aura', cor: '#2e77bc', corLight: '#4e97dc' },
  { id: '09', nome: 'Cabal', cor: '#2e77bc', corLight: '#4e97dc' },
  { id: '99', nome: 'Outros', cor: '#666666', corLight: '#888888' },
];

const formatCurrency = (value?: number) => {
  if (!value && value !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

export const DialogNFE: React.FC<PaymentDialogProps> = (props: PaymentDialogProps) => {
  const { onClose, open, selectedId, idpar, numerodocumento, valortotal, parceironome, emissaodata, selectedtpag } = props;
  const [openmsg, setOpenmsg] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmitting, setIsEmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [emittingProgress, setEmittingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Emitindo nota fiscal...');

  const [idcontaError, setIdcontaError] = useState('');
  const [tms, setTms] = useState<'error' | 'success' | 'warning' | 'info'>('info');
  // Botão "Verificar Nota" — mesma checagem de configuração tributária dos
  // itens (NCM/CFOP/origem/CST/CST PIS/CST COFINS) e do endereço do cliente
  // que já existe na NFC-e (ver VerificarNfe.ts no backend).
  const [verificandoNota, setVerificandoNota] = useState(false);
  const theme = useTheme();

  const contaInputRef = useRef<HTMLInputElement>(null);
  const bandeiraSelectRef = useRef<HTMLInputElement>(null);
  const cautInputRef = useRef<HTMLInputElement>(null);
  const emitirButtonRef = useRef<HTMLButtonElement>(null);
  const paymentSelectorRef = useRef<HTMLDivElement>(null);
  // Confirmar uma forma por CLIQUE deve manter o foco na listagem (o
  // operador espera continuar navegando com ↑↓ logo em seguida) — só o
  // Enter (teclado) e os atalhos numéricos avançam pro próximo campo
  // obrigatório sozinhos (ver Dialogsendnfce.tsx, mesmo ajuste).
  const avancarFocoAutomaticoRef = useRef(true);
  // Conta bancária padrão pra PIX/Transferência, configurada em Configurações
  // do PDV (idcontapadraopix) — buscada uma vez ao abrir a dialog e usada só
  // pra pré-preencher o autocomplete de conta ao selecionar essas formas (ver
  // selecionarFormaPagamento). Ref, não state: não precisa re-renderizar nada
  // sozinha, só ser lida no momento da seleção.
  const contaPadraoPixRef = useRef('');

  const resetStates = () => {
    setOpenmsg(false);
    setMsg('');
    setTms('info');
    setIsEmitting(false);
    setIsLoading(false);
    setEmittingProgress(0);
    setLoadingMessage('Emitindo nota fiscal...');
    avancarFocoAutomaticoRef.current = true;
    setPagamento({
      id: '',
      idparceiro: '',
      idconta: Environment.URL_IDCONTA || '',
      indpag: 0,
      tpag: '01',
      tband: '00',
      tcnpj: '',
      caut: '',
      promocao: false,
    });
  };

  const [pagamento, setPagamento] = useState<IPagamentoData>({
    id: selectedId || '',
    idparceiro: idpar || '',
    idconta: Environment.URL_IDCONTA || '',
    indpag: 0,
    tpag: '01',
    tband: '00',
    tcnpj: '',
    caut: '',
    promocao: false,
  });

  // Forma "sob o cursor" ao navegar com seta — só vira de fato pagamento.tpag
  // quando o operador confirma com Enter (ver PaymentMethodSelector.tsx).
  const [formaDestacada, setFormaDestacada] = useState(pagamento.tpag);
  useEffect(() => {
    setFormaDestacada(pagamento.tpag);
  }, [pagamento.tpag]);

  useEffect(() => {
    setPagamento(prev => ({
      ...prev,
      id: selectedId || '',
      idparceiro: idpar || '',
    }));
  }, [selectedId, open, idpar]);

  useEffect(() => {
    if (!open) {
      resetStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Foca a lista de formas de pagamento assim que a dialog abre — sem isso,
  // as setas não navegam em lugar nenhum até o operador clicar na lista.
  useEffect(() => {
    if (!open) return;
    const timeoutId = setTimeout(() => paymentSelectorRef.current?.focus(), 100);
    return () => clearTimeout(timeoutId);
  }, [open]);

  // Conta padrão de PIX/Transferência configurada no PDV — buscada uma vez
  // ao abrir, pra já estar disponível quando o operador selecionar uma
  // dessas formas (ver selecionarFormaPagamento/contaPadraoPixRef).
  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    ConfiguracaoPdvService.get().then((resultado) => {
      if (cancelado || resultado instanceof Error) return;
      contaPadraoPixRef.current = resultado.idcontapadraopix || '';
    });
    return () => { cancelado = true; };
  }, [open]);

  const isPagamentoCartao = pagamento.tpag === '03' || pagamento.tpag === '04';
  const isPagamentoContaDestino = pagamento.tpag === '20' || pagamento.tpag === '18';
  // Conta só é escolhida manualmente pra PIX Estático/Transferência —
  // Dinheiro, PIX Dinâmico e Cartão Crédito/Débito sempre roteiam pra uma
  // conta FIXA no backend (ver Dialogsendnfce.tsx, mesmo ajuste).
  const shouldShowConta = isPagamentoContaDestino;

  const simulateProgress = () => {
    setEmittingProgress(0);
    const interval = setInterval(() => {
      setEmittingProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 800);
    return interval;
  };

  const handleConfirmarPagamento = async (dadosPagamento: IPagamentoData) => {
    if (isEmitting || isLoading) {
      setTms('warning');
      setMsg('Aguarde, já existe uma emissão em andamento...');
      setOpenmsg(true);
      return;
    }

    if (dadosPagamento.idparceiro === Environment.URL_IDPARCEIRO) {
      setTms('error');
      setMsg('Vendas do parceiro à vista não podem emitir NFE, apenas NFC-e.');
      setOpenmsg(true);
      return;
    }

    try {
      const dadosValidados = await pagamentoSchema.validate(dadosPagamento, {
        abortEarly: false,
        stripUnknown: true,
      });

      if (!dadosValidados.id) return;

      const dadosCompletos: IPagamentoData = {
        ...dadosValidados,
        // Promoção saiu da tela de emissão fiscal — decisão de
        // desconto/promoção já deve estar definida antes de chegar aqui.
        promocao: false,
      };

      setIsEmitting(true);
      setIsLoading(true);
      setLoadingMessage('Validando dados da nota fiscal...');

      const progressInterval = simulateProgress();

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Tempo limite excedido. Tente novamente.')), 60000);
      });
      const servicePromise = FaturamentoService.createnfe(dadosCompletos);
      const resultado = await Promise.race([servicePromise, timeoutPromise]);

      clearInterval(progressInterval);
      setEmittingProgress(100);
      setLoadingMessage('Nota emitida com sucesso!');

      if (typeof resultado === 'string' && resultado.startsWith('JVBERi0x')) {
        const dataUrl = `data:application/pdf;base64,${resultado}`;
        const novaAba = window.open(dataUrl, '_blank');
        if (!novaAba) {
          setTms('error');
          setMsg('Permita pop-ups para visualizar a NFe');
          setIsEmitting(false);
          setIsLoading(false);
          return;
        }
        setTms('success');
        setMsg('NFe gerada com sucesso!');
        setTimeout(() => {
          setIsEmitting(false);
          setIsLoading(false);
          onClose();
        }, 1500);
      }
      else if (resultado instanceof Blob) {
        const url = URL.createObjectURL(resultado);
        const novaAba = window.open(url, '_blank');
        if (!novaAba) {
          setTms('error');
          setMsg('Permita pop-ups para visualizar a NFe');
          setIsEmitting(false);
          setIsLoading(false);
          return;
        }
        setTms('success');
        setMsg('NFe gerada com sucesso!');
        setTimeout(() => {
          URL.revokeObjectURL(url);
          setIsEmitting(false);
          setIsLoading(false);
          onClose();
        }, 1500);
      }
      else if (resultado instanceof Error) {
        setTms('error');
        setMsg(resultado.message || 'Erro ao gerar NFe');
        setIsEmitting(false);
        setIsLoading(false);
      }
      else {
        setTms('error');
        setMsg('Resposta inesperada do servidor');
        setIsEmitting(false);
        setIsLoading(false);
      }
    } catch (error: unknown) {
      console.error('Erro na emissão:', error);
      setIsEmitting(false);
      setIsLoading(false);

      if (error instanceof Error) {
        if (error.message === 'Tempo limite excedido. Tente novamente.') {
          setTms('error');
          setMsg('A emissão está demorando mais que o esperado. Verifique sua conexão e tente novamente.');
        } else {
          setTms('error');
          setMsg(error.message || 'Erro ao gerar NFe');
        }
      } else if (error && typeof error === 'object' && 'name' in error && error.name === 'ValidationError') {
        const validationError = error as { errors?: string[] };
        setTms('error');
        setMsg(validationError.errors?.join('\n') || 'Erro de validação');
      } else {
        setTms('error');
        setMsg('Erro inesperado ao gerar NFe. Tente novamente.');
      }
    } finally {
      setOpenmsg(true);
    }
  };

  const handleCancel = () => {
    if (isEmitting) {
      setTms('warning');
      setMsg('Aguarde a conclusão da emissão antes de cancelar.');
      setOpenmsg(true);
      return;
    }
    resetStates();
    onClose();
  };

  const handleVerificarNota = async () => {
    if (!pagamento.id || verificandoNota || isEmitting) return;
    setVerificandoNota(true);
    const resultado = await FaturamentoService.verificarNfe(pagamento.id, pagamento.idparceiro);
    setVerificandoNota(false);

    if (resultado instanceof Error) {
      setTms('error');
      setMsg(resultado.message);
      setOpenmsg(true);
      return;
    }
    if (resultado.valido) {
      setTms('success');
      setMsg('Nenhum problema encontrado na configuração tributária dos itens ou no cadastro do cliente.');
    } else {
      setTms('warning');
      setMsg(
        'Configuração tributária/cadastro incompleto:\n' +
        (resultado.problemas || []).map((problema) => `• ${problema}`).join('\n')
      );
    }
    setOpenmsg(true);
  };

  const selecionarFormaPagamento = (novaForma: string) => {
    if (isEmitting) return;
    const exigeContaManual = novaForma === '20' || novaForma === '18';
    // Conta manual (PIX Estático/Transferência) já vem pré-preenchida com a
    // conta padrão de PIX configurada no PDV (idcontapadraopix, ver
    // contaPadraoPixRef acima), quando existir — o operador só precisa
    // trocar se quiser outra conta.
    setPagamento((prev) => ({
      ...prev,
      tpag: novaForma,
      tband: '00',
      caut: '',
      idconta: exigeContaManual ? contaPadraoPixRef.current : (Environment.URL_IDCONTA || ''),
      promocao: novaForma === '01' ? prev.promocao : false,
    }));
    setIdcontaError('');
  };

  // Mesma auto-seleção de conta única da dialog de NFC-e — ver
  // Dialogsendnfce.tsx.
  useEffect(() => {
    if (!shouldShowConta || pagamento.idconta) return;
    let cancelado = false;
    const tipoFiltro = isPagamentoContaDestino ? 'banco' : undefined;
    ContaBancariaService.getAllList('', '', tipoFiltro, 50).then((result) => {
      if (cancelado || result instanceof Error) return;
      if (result.data.length === 1) {
        setPagamento((prev) => (prev.idconta ? prev : { ...prev, idconta: result.data[0].idconta }));
      }
    });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagamento.tpag, shouldShowConta]);

  const focarProximoCampoObrigatorio = () => {
    if (shouldShowConta && !pagamento.idconta) { contaInputRef.current?.focus(); return; }
    if (isPagamentoCartao && (!pagamento.tband || pagamento.tband === '00')) { bandeiraSelectRef.current?.focus(); return; }
    if (isPagamentoCartao && !pagamento.caut) { cautInputRef.current?.focus(); return; }
    emitirButtonRef.current?.focus();
  };

  useEffect(() => {
    if (!avancarFocoAutomaticoRef.current) return;
    if (shouldShowConta && !pagamento.idconta) return;
    const timeoutId = setTimeout(focarProximoCampoObrigatorio, 60);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagamento.tpag, pagamento.idconta]);

  const handleBandeiraChange = (event: SelectChangeEvent<string>) => {
    if (isEmitting) return;
    const { value } = event.target;
    setPagamento((prev) => ({ ...prev, tband: value }));
    // Reage à MUDANÇA DE VALOR, não ao keydown do <Select> — com o menu
    // aberto, o Enter que confirma a bandeira é tratado pelo Menu/Popover do
    // MUI antes de chegar no onKeyDown do próprio Select, então esse nunca
    // disparava (ver mesmo comentário em Dialogsendnfce.tsx).
    if (value) setTimeout(() => cautInputRef.current?.focus(), 80);
  };

  const handleCodigoTransacaoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isEmitting) return;
    setPagamento((prev) => ({ ...prev, caut: event.target.value }));
  };

  const isConfirmDisabled = isEmitting || isLoading || (shouldShowConta && !pagamento.idconta);

  // Mesma orquestração de teclado da dialog de NFC-e (ver Dialogsendnfce.tsx)
  // — sem F6 aqui de propósito: NFe não tem etapa de identificação do
  // consumidor nessa dialog (o parceiro já é conhecido, ver idpar), então
  // não há nada pra esse atalho fazer ainda; a estrutura já suporta
  // adicionar depois sem mexer no resto.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        handleCancel();
        return;
      }

      if (isEventoDeDigitacao(event.target)) return;

      if (event.key === 'F12') {
        event.preventDefault();
        event.stopPropagation();
        if (!isConfirmDisabled) handleConfirmarPagamento(pagamento);
        return;
      }
      if (event.key === 'F4') {
        if (!shouldShowConta) return;
        event.preventDefault();
        event.stopPropagation();
        contaInputRef.current?.focus();
        return;
      }
      const numero = Number(event.key);
      if (numero >= 1 && numero <= TODAS_FORMAS_PAGAMENTO.length) {
        event.preventDefault();
        event.stopPropagation();
        avancarFocoAutomaticoRef.current = true;
        selecionarFormaPagamento(TODAS_FORMAS_PAGAMENTO[numero - 1].id);
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEmitting, isLoading, pagamento, shouldShowConta]);

  const corBg = theme.torq.sidebar.bg;
  const corAccent = theme.torq.sidebar.iconActive;
  const corBorda = theme.palette.mode === 'dark' ? theme.palette.divider : '#DCE5EA';

  return (
    <>
      {isEmitting && (
        <FullScreenLoading message={loadingMessage} progress={emittingProgress} />
      )}

      <Snackbar
        open={openmsg}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        // Erro fica aberto até o operador fechar — com a tradução dos erros
        // de validação fiscal (ver traduzirErroFiscal), a mensagem pode virar
        // uma lista de vários campos, e 6s não dá tempo de ler.
        autoHideDuration={tms === 'success' ? 6000 : null}
        onClose={() => setOpenmsg(false)}
      >
        <Alert
          severity={tms}
          onClose={() => setOpenmsg(false)}
          variant="filled"
          sx={{ width: '100%', maxWidth: 500, borderRadius: 2, boxShadow: theme.shadows[3], whiteSpace: 'pre-line' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth={false}
        PaperProps={{ sx: { width: 'min(640px, 94vw)', borderRadius: '20px', overflow: 'hidden', maxHeight: '92vh' } }}
      >
        {/* HEADER — gradiente escuro com dois feixes diagonais (pseudo-elementos), sem imagem
            externa: ::before cria a faixa mais escura, ::after a faixa azul translúcida. */}
        <Box
          sx={{
            position: 'relative',
            minHeight: 118,
            px: '34px', py: '26px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            overflow: 'hidden',
            background: 'linear-gradient(110deg, #001e27 0%, #032b3c 48%, #0b2948 100%)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -80, left: '42%',
              width: 260, height: 250,
              background: 'rgba(0, 16, 32, 0.34)',
              transform: 'rotate(33deg)',
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -120, right: '6%',
              width: 420, height: 290,
              background: 'linear-gradient(135deg, rgba(44, 107, 156, 0.18), rgba(44, 107, 156, 0.02))',
              pointerEvents: 'none',
            },
            '& > *': { position: 'relative', zIndex: 2 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: '12px',
                bgcolor: alpha(corAccent, 0.16),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: corAccent,
              }}
            >
              <ReceiptIcon />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, lineHeight: 1.2, color: '#FFFFFF' }}>
                Emitir NFe
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.65) }}>
                Nota Fiscal Eletrônica
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                px: 1, py: 0.4, borderRadius: '6px',
                border: `1px solid ${alpha('#FFFFFF', 0.3)}`,
                fontSize: '0.7rem', fontWeight: 700, color: alpha('#FFFFFF', 0.85),
              }}
            >
              ESC
            </Box>
            <IconButton
              onClick={handleCancel}
              disabled={isEmitting}
              size="small"
              sx={{ color: '#FFFFFF', '&:hover': { bgcolor: alpha('#FFFFFF', 0.1) } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* RESUMO DA VENDA */}
        <DocumentoResumoBar
          numerodocumento={numerodocumento}
          emissaodata={emissaodata}
          parceironome={parceironome}
          valortotal={valortotal}
        />

        <DialogContent sx={{ p: 0 }}>
          {isLoading && !isEmitting && (
            <LinearProgress color="primary" sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />
          )}

          <Box sx={{ p: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            {/* FORMAS DE PAGAMENTO — coluna própria à esquerda, com os dados
                da forma selecionada (conta bancária/cartão) ao lado, não
                embaixo (ver PaymentMethodSelector.tsx). */}
            <Box sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}>
              <PaymentMethodSelector
                ref={paymentSelectorRef}
                formas={TODAS_FORMAS_PAGAMENTO}
                selecionadoId={pagamento.tpag}
                destacadoId={formaDestacada}
                onDestacar={setFormaDestacada}
                onConfirmar={(id, viaTeclado) => {
                  avancarFocoAutomaticoRef.current = viaTeclado;
                  selecionarFormaPagamento(id);
                  if (viaTeclado) setTimeout(focarProximoCampoObrigatorio, 60);
                }}
                disabled={isEmitting}
                orientation="vertical"
              />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
            {(shouldShowConta || isPagamentoCartao) ? (
              <Box sx={{ border: `1px solid ${corBorda}`, borderRadius: '14px', overflow: 'hidden' }}>
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 2, py: 1.25,
                    bgcolor: alpha(corAccent, 0.06),
                    borderBottom: `1px solid ${corBorda}`,
                  }}
                >
                  <Box sx={{ color: corAccent, display: 'flex' }}>
                    {TODAS_FORMAS_PAGAMENTO.find((f) => f.id === pagamento.tpag)?.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                    {TODAS_FORMAS_PAGAMENTO.find((f) => f.id === pagamento.tpag)?.nome}
                  </Typography>
                </Box>

                {shouldShowConta && (
                  // Linha própria, largura total — encolhida ao lado do
                  // título no cabeçalho, o nome do banco/titular ficava
                  // cortado (ver Dialogsendnfce.tsx, mesmo ajuste).
                  <Box sx={{ px: 2, pt: 2, pb: isPagamentoCartao ? 0 : 2 }}>
                    <AutoCompleteContaBancariaSimple
                      label={isPagamentoContaDestino ? 'Conta bancária de destino' : 'Conta'}
                      value={pagamento.idconta}
                      tipo={isPagamentoContaDestino ? 'banco' : undefined}
                      inputRef={contaInputRef}
                      onChange={(idconta) => {
                        setPagamento({ ...pagamento, idconta });
                        setIdcontaError('');
                      }}
                      isExternalLoading={isLoading}
                      disabled={isEmitting}
                      error={!!idcontaError}
                      helperText={idcontaError}
                      fullWidth
                    />
                  </Box>
                )}

                {isPagamentoCartao && (
                  // Empilhados um em cima do outro, em ordem (Bandeira depois
                  // Código da Transação) — a dialog ficou estreita demais pra
                  // caber os dois lado a lado sem cortar o texto.
                  <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="nfe-bandeira-label" shrink sx={{ fontSize: '0.7rem' }}>Bandeira do Cartão</InputLabel>
                      <Select
                        inputRef={bandeiraSelectRef}
                        labelId="nfe-bandeira-label"
                        size="small"
                        value={pagamento.tband || ''}
                        onChange={handleBandeiraChange}
                        disabled={isEmitting}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {BANDEIRAS_CARTAO.map((bandeira) => (
                          <MenuItem key={bandeira.id} value={bandeira.id} sx={{ gap: 1.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: theme.palette.mode === 'dark' ? bandeira.corLight : bandeira.cor }} />
                            <Typography fontSize="0.875rem">{bandeira.nome}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      inputRef={cautInputRef}
                      size="small"
                      fullWidth
                      sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                      label="Código da Transação"
                      value={pagamento.caut || ''}
                      onChange={handleCodigoTransacaoChange}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focarProximoCampoObrigatorio(); } }}
                      placeholder="Digite o código da transação"
                      disabled={isEmitting}
                    />
                  </Box>
                )}
              </Box>
              ) : (
                <FormaProntaPanel
                  titulo={
                    pagamento.tpag === '01'
                      ? 'Pagamento em dinheiro'
                      : `${TODAS_FORMAS_PAGAMENTO.find((f) => f.id === pagamento.tpag)?.nome} selecionado`
                  }
                  descricao={
                    pagamento.tpag === '01'
                      ? 'Entra direto no caixa da sessão ativa de quem está emitindo — nenhuma conta ou dado extra precisa ser informado.'
                      : 'Nenhuma informação adicional é necessária para essa forma de pagamento.'
                  }
                  documentoLabel="para emitir a NFe"
                />
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3, py: 1.75,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', rowGap: 1.5,
            borderTop: `1px solid ${corBorda}`,
            bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#F8FAFC',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <KeyHintRow keyLabel="↑↓">Navegar</KeyHintRow>
            <KeyHintRow keyLabel="ENTER">Selecionar</KeyHintRow>
            {shouldShowConta && <KeyHintRow keyLabel="F4">Alterar conta</KeyHintRow>}
            <KeyHintRow keyLabel="F12">Emitir NFe</KeyHintRow>
            <KeyHintRow keyLabel="ESC">Cancelar</KeyHintRow>
          </Box>
          {/* flexShrink: 0 + whiteSpace: nowrap — sem isso, quando o espaço
              fica curto (chips de atalho à esquerda competindo pela mesma
              linha), o flexbox encolhia esses botões até o texto quebrar em
              duas linhas, deformando a pílula arredondada. */}
          <Box sx={{ display: 'flex', gap: 1.25, flexShrink: 0 }}>
            <Button
              onClick={handleVerificarNota}
              variant="outlined"
              color="secondary"
              disabled={isEmitting || verificandoNota || !pagamento.id}
              startIcon={verificandoNota ? <CircularProgress size={16} /> : <FactCheckIcon fontSize="small" />}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Verificar Nota
            </Button>
            <Button
              onClick={handleCancel}
              variant="outlined"
              color="inherit"
              disabled={isEmitting}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              <CloseIcon fontSize="small" sx={{ mr: 0.5 }} /> Esc Cancelar
            </Button>
            <Button
              ref={emitirButtonRef}
              onClick={() => handleConfirmarPagamento(pagamento)}
              variant="contained"
              disabled={isConfirmDisabled}
              startIcon={isEmitting ? <CircularProgress size={16} sx={{ color: theme.torq.sidebar.bg }} /> : <ReceiptIcon fontSize="small" />}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                bgcolor: corAccent,
                color: corBg,
                '&:hover': { bgcolor: alpha(corAccent, 0.85) },
                '&.Mui-disabled': { bgcolor: alpha(corAccent, 0.35), color: alpha(corBg, 0.6) },
              }}
            >
              {isEmitting ? 'Emitindo NFe...' : 'F12  Emitir NFe'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};
